import {
    afterEveryRender,
    ChangeDetectionStrategy,
    Component,
    computed,
    DestroyRef,
    ElementRef,
    effect,
    inject,
    input,
    output,
} from '@angular/core';

import { addDescribedBy, removeDescribedBy } from '../../utils/described-by';
import { PopoverComponent } from '../popover/popover.component';

let nextTooltipId = 0;

@Component({
    selector: 'app-tooltip',
    templateUrl: './tooltip.component.html',
    styleUrls: ['../popover/popover.component.scss', './tooltip.component.scss'],
    host: {
        '(mouseenter)': 'onMouseEnter()',
        '(mouseleave)': 'onMouseLeave()',
        '(focusin)': 'onFocusIn()',
        '(focusout)': 'onFocusOut($event)',
        '(document:keydown.escape)': 'onEscape($event)',
    },
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TooltipComponent extends PopoverComponent {
    readonly disabled = input(false);
    readonly forceOpen = input(false);
    readonly openOnInteraction = input(true);
    readonly openedChange = output<boolean>();
    protected readonly tooltipId = computed(() => this.contentId() || `app-tooltip-${this.generatedId}`);

    private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);
    private readonly tooltipDestroyRef = inject(DestroyRef);
    private readonly generatedId = nextTooltipId++;
    private describedTrigger: HTMLElement | null = null;
    private describedTooltipId: string | null = null;
    private ownsDescribedById = false;
    private hovered = false;
    private focused = false;
    private dismissed = false;

    constructor() {
        super();

        effect(() => {
            this.disabled();
            this.forceOpen();
            this.openOnInteraction();
            this.syncOpenState();
        });

        afterEveryRender({
            mixedReadWrite: () => this.syncTriggerDescription(),
        });

        this.tooltipDestroyRef.onDestroy(() => this.clearTriggerDescription());
    }

    override toggle(): void {
        this.setOpenState(!this.disabled() && !this.isOpen());
    }

    override open(): void {
        this.setOpenState(!this.disabled());
    }

    override close(): void {
        this.setOpenState(false);
    }

    protected onMouseEnter(): void {
        this.hovered = true;
        this.syncOpenState();
    }

    protected onMouseLeave(): void {
        this.hovered = false;
        this.syncOpenState();
    }

    protected onFocusIn(): void {
        this.focused = true;
        this.syncOpenState();
    }

    protected onFocusOut(event: FocusEvent): void {
        this.focused = event.relatedTarget instanceof Node && this.host.nativeElement.contains(event.relatedTarget);
        this.syncOpenState();
    }

    protected onEscape(event: Event): void {
        if (!this.isOpen()) return;

        event.preventDefault();
        this.dismissed = this.hovered || this.focused;
        this.close();
    }

    private syncOpenState(): void {
        const interacting = this.hovered || this.focused;
        if (!interacting) this.dismissed = false;

        this.setOpenState(
            !this.disabled() && !this.dismissed && (this.forceOpen() || (this.openOnInteraction() && interacting)),
        );
    }

    private setOpenState(open: boolean): void {
        if (this.isOpen() === open) return;

        this.isOpen.set(open);
        this.openedChange.emit(open);
    }

    private syncTriggerDescription(): void {
        if (this.disabled()) {
            this.clearTriggerDescription();
            return;
        }

        const tooltipId = this.tooltipId();
        const trigger: HTMLElement | null = this.host.nativeElement.querySelector(':scope > [popover-trigger]');

        if (trigger !== this.describedTrigger || tooltipId !== this.describedTooltipId) {
            this.clearTriggerDescription();
            this.describedTrigger = trigger;
            this.describedTooltipId = tooltipId;
        }

        if (!trigger) return;

        if (addDescribedBy(trigger, tooltipId)) this.ownsDescribedById = true;
    }

    private clearTriggerDescription(): void {
        if (this.describedTrigger && this.describedTooltipId && this.ownsDescribedById) {
            removeDescribedBy(this.describedTrigger, this.describedTooltipId);
        }

        this.describedTrigger = null;
        this.describedTooltipId = null;
        this.ownsDescribedById = false;
    }
}
